import { V2 } from '../theme/tokens'
// frontend/components/ProductPortfolioDashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Tooltip,
  Button,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KpiRow, { KpiCell } from './common/KpiRow';
import StatTile from './common/StatTile';
import { DonutCard } from './dashboard/DashboardKit';
import { scrollIntoViewRespectingMotion } from '../utils/scroll';
import { formatPercent } from '../utils/format';

const API = process.env.REACT_APP_API_BASE_URL || '';

const pct = (x, d=1) => formatPercent(Number(x||0), { digits: d });

export default function ProductPortfolioDashboard() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dists, setDists] = useState(null);
  const [overview, setOverview] = useState(null);
  // Track the /api/product_overview fetch independently — it feeds the primary
  // KPI row. While in flight we render '–' (not misleading computed-from-empty
  // zeros); on rejection we show '–' + a non-silent "couldn't load" caption.
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(false);

  // Emit a SKU subset to the products table (reused by KPI click-to-filter
  // and the taxonomy widget). ProductsTable listens for this event.
  const emitSkuFilter = (skus, key) => {
    const arr = Array.from(
      new Set(
        (skus || [])
          .map((entry) => (typeof entry === 'string' ? entry : entry?.sku))
          .filter(Boolean)
      )
    );
    if (!arr.length) return;
    window.dispatchEvent(
      new CustomEvent('portfolio-dashboard:apply-filter', { detail: { skus: arr, key } })
    );

    const anchor = document.getElementById('products-table-anchor');
    if (anchor) scrollIntoViewRespectingMotion(anchor, { block: 'start' });
  };

  const refresh = async () => {
    setLoading(true);
    setOverviewLoading(true);
    setOverviewError(false);
    const calls = [
      fetch(`${API}/api/portfolio/products/summary`).then((r) => (r.ok ? r.json() : Promise.reject())),
      fetch(`${API}/api/portfolio/products/distributions`).then((r) => (r.ok ? r.json() : Promise.reject())),
      fetch(`${API}/api/product_overview`).then((r) => (r.ok ? r.json() : Promise.reject())),
    ];
    const [s, d, o] = await Promise.allSettled(calls);
    if (s.status === 'fulfilled') setSummary(s.value);   else console.warn('summary failed');
    if (d.status === 'fulfilled') setDists(d.value);     else console.warn('distributions failed');
    if (o.status === 'fulfilled') {
      setOverview(o.value);
    } else {
      console.warn('product_overview failed');
      setOverviewError(true);
    }
    setOverviewLoading(false);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  useEffect(() => { refresh(); }, []);

  const total = summary?.counts?.total_products || 0;
  const abcd = useMemo(() => dists?.abcd_count || {}, [dists]);
  const marginCats = dists?.margin_tiers || {};

  // --- New primary KPIs, computed client-side from /api/product_overview ---
  // These counts are over product_overview's "active" set (status != archived,
  // not deleted) — looser than summary.counts.total_products; don't reconcile.
  const overviewRows = useMemo(
    () => (Array.isArray(overview) ? overview : []),
    [overview]
  );

  const kpis = useMemo(() => {
    // Exclude bundle parents from ALL KPI aggregates. A native Shopify bundle
    // appears as one deduped product_overview row (Phase 1) with NULL/0
    // inventory + sales — counting it would wrongly inflate the out-of-stock
    // count and depress units-sold / momentum. The bundle row stays VISIBLE in
    // the product list; it just doesn't count toward these health KPIs.
    const rows = overviewRows.filter((r) => !r?.is_bundle);

    // Units sold (30d): Σ(daily 30d rate) × 30, rounded. Display-only.
    const rate30 = rows.reduce((acc, r) => acc + Number(r?.average_sales_30d ?? 0), 0);
    const rate90 = rows.reduce((acc, r) => acc + Number(r?.average_sales_90d ?? 0), 0);
    const unitsSold30d = Math.round(rate30 * 30);

    // Sales momentum: guarded pct change of aggregate daily rate (30d vs 90d).
    const momentumPct = rate90 > 0 ? Math.round(((rate30 - rate90) / rate90) * 100) : null;

    // Out of stock: current_inventory <= 0. Clickable.
    const oosRows = rows.filter((r) => Number(r?.current_inventory ?? 0) <= 0);
    const oosSkus = oosRows.map((r) => r?.sku).filter(Boolean);

    // Low stock (<= 14d): estimated_stockout_date within 14 days of today.
    // SAME predicate as ProductsTable's `stockouts` quick-filter (no inventory gate).
    const today = new Date();
    const inDays = (d) => (d - today) / (1000 * 60 * 60 * 24);
    const lowStockRows = rows.filter((r) => {
      const raw = r?.estimated_stockout_date;
      const sd = raw ? new Date(raw) : null;
      return sd && isFinite(sd) && inDays(sd) <= 14;
    });
    const lowStockSkus = lowStockRows.map((r) => r?.sku).filter(Boolean);

    return {
      unitsSold30d,
      momentumPct,
      oosCount: oosRows.length,
      oosSkus,
      lowStockCount: lowStockRows.length,
      lowStockSkus,
    };
  }, [overviewRows]);

  // The primary KPI row is ready to show real numbers only once the overview
  // fetch resolved successfully. Until then values render as '–'.
  const kpiReady = !overviewLoading && !overviewError;
  // Coerce a computed KPI value for display: '–' while loading or on error,
  // so we never surface misleading computed-from-empty zeros.
  const kpiValue = (v) => (kpiReady ? v : '–');

  // Momentum: signed % is the PRIMARY value; "30d vs 90d" is the label/hint.
  // '—' for the null (no-90d-baseline) case. Green up / red down preserved.
  const momentumReady = kpiReady && kpis.momentumPct !== null;
  const momentumUp = momentumReady && kpis.momentumPct >= 0;
  const momentumValue = !kpiReady
    ? '–'
    : kpis.momentumPct === null
      ? '—'
      : `${momentumUp ? '↑' : '↓'}${Math.abs(kpis.momentumPct)}%`;
  const momentumColor = momentumReady
    ? (momentumUp ? 'success.main' : 'error.main')
    : 'text.secondary';

  const categoryItems = useMemo(() => {
    if (!total) return [];
    return ['A', 'B', 'C', 'D']
      .map((key) => {
        const value = abcd[key] || 0;
        if (!value) return null;
        return {
          name: key,
          value,
          label: key,
          meta: formatPercent(value / total, { digits: 1 }),
        };
      })
      .filter(Boolean);
  }, [abcd, total]);

  const stockCoverageBuckets = useMemo(
    () => Object.entries(dists?.stock_coverage_hist || {}),
    [dists]
  );

  return (
    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6">Portfolio Dashboard</Typography>
        <Tooltip
          title="Aggregates from Products (margins & categories), order revenue over the last complete calendar month, and stockout report for coverage."
          arrow
        >
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={refresh}>
          Refresh
        </Button>
      </Stack>

      <KpiRow>
        <KpiCell>
          <StatTile
            label="Units sold (30d)"
            value={kpiValue(kpis.unitsSold30d)}
            hint={overviewError ? "Couldn't load — try Refresh" : undefined}
            labelTooltip="Estimated units sold over the last 30 days: sum of daily sales rate × 30 across active products."
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            label="Sales momentum"
            value={momentumValue}
            valueColor={momentumColor}
            hint={overviewError ? "Couldn't load — try Refresh" : '30d vs 90d'}
            labelTooltip="Change in aggregate daily sales rate: last 30d vs last 90d. ↑ accelerating, ↓ slowing."
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            label="Out of stock"
            value={kpiValue(kpis.oosCount)}
            hint={overviewError ? "Couldn't load — try Refresh" : undefined}
            onClick={kpiReady && kpis.oosSkus.length ? () => emitSkuFilter(kpis.oosSkus, 'kpi:oos') : undefined}
            ariaLabel="Filter table to out-of-stock products"
            labelTooltip="Active products with on-hand inventory at or below zero. Click to filter the table."
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            label="Low stock (≤14d)"
            value={kpiValue(kpis.lowStockCount)}
            hint={overviewError ? "Couldn't load — try Refresh" : undefined}
            onClick={kpiReady && kpis.lowStockSkus.length ? () => emitSkuFilter(kpis.lowStockSkus, 'kpi:low_stock') : undefined}
            ariaLabel="Filter table to low-stock products"
            labelTooltip="Active products predicted to stock out within 14 days. Click to filter the table."
          />
        </KpiCell>
      </KpiRow>

      <KpiRow sx={{ mt: 2 }}>
        <KpiCell>
          <DonutCard
            label="Category mix (A–D)"
            items={categoryItems}
            loading={loading}
            palette={['#0d9488', V2.infoText, V2.warnText, V2.ink45]}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Revenue share: A {pct(summary?.categories?.revenue_share?.A || 0, 0)} · B{' '}
            {pct(summary?.categories?.revenue_share?.B || 0, 0)} · C{' '}
            {pct(summary?.categories?.revenue_share?.C || 0, 0)} · D{' '}
            {pct(summary?.categories?.revenue_share?.D || 0, 0)}
          </Typography>
        </KpiCell>
        <KpiCell>
          <Typography variant="caption" color="text.secondary">
            Markup tiers (on cost)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            From markup on cost (net / cost − 1) — not the same scale as the
            &ldquo;Margin % (of net)&rdquo; column in Products.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
            {Object.entries(marginCats).map(([k, v]) => (
              <Chip
                key={k}
                size="small"
                label={`${k}: ${v}`}
                variant={k === 'unknown' ? 'outlined' : 'filled'}
                color={k === 'high' ? 'success' : k === 'low' ? 'warning' : 'default'}
              />
            ))}
          </Stack>
        </KpiCell>
        <KpiCell>
          <Typography variant="caption" color="text.secondary">
            Stock coverage (days)
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {stockCoverageBuckets.map(([bucket, count]) => {
              const pctWidth = total ? Math.max(4, Math.round((count / total) * 100)) : 4;
              return (
                <Box key={bucket} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 60 }}>
                    <Typography variant="caption">{bucket}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Box
                      sx={{
                        width: `${pctWidth}%`,
                        bgcolor: 'primary.main',
                        height: 10,
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ width: 32, textAlign: 'right' }}>
                    {count}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </KpiCell>
      </KpiRow>
    </Box>
  );
}
